<template>
    <div class="space-y-[28px]">
        <!-- Step 1 - Configure your record settings -->
        <div>
            <div class="mb-[14px] flex items-start gap-3">
                <span class="step-badge">1</span>
                <div>
                    <div
                        class="text-[12px] font-semibold leading-[140%] text-gray-800"
                    >
                        {{ $t("vue.step_configure_settings_title") }}
                    </div>
                    <p class="text-[10.5px] leading-[142%] text-gray-500">
                        {{ $t("vue.step_configure_settings_desc") }}
                    </p>
                </div>
            </div>

            <div class="grid gap-[24px]">
                <div
                    class="grid grid-cols-[repeat(3,minmax(180px,1fr))] gap-[15px] max-[860px]:grid-cols-1"
                >
                    <div class="flex flex-col">
                        <div
                            class="mb-[6px] text-[12px] font-[500] leading-[16.8px] text-black"
                        >
                            {{ $t("vue.dmarc_record_policy_label") }}
                        </div>
                        <select v-model="ui.p" class="dmarc-control">
                            <option value="none">
                                {{ $t("vue.dmarc_record_option_none_monitor") }}
                            </option>
                            <option value="quarantine">
                                {{ $t("vue.dmarc_record_option_quarantine") }}
                            </option>
                            <option value="reject">
                                {{ $t("vue.dmarc_record_option_reject") }}
                            </option>
                        </select>
                        <div class="dmarc-hint">
                            {{ $t("vue.dmarc_record_policy_hint") }}
                        </div>
                    </div>

                    <div class="flex flex-col">
                        <div
                            class="mb-[6px] text-[12px] font-[500] leading-[16.8px] text-black"
                        >
                            {{ $t("vue.dmarc_record_pct_label") }}
                        </div>
                        <input
                            v-model="ui.pct"
                            class="dmarc-control"
                            type="number"
                            min="0"
                            max="100"
                            step="1"
                        />
                        <div class="dmarc-hint">
                            {{ $t("vue.dmarc_record_pct_hint") }}
                        </div>
                    </div>

                    <div class="flex flex-col">
                        <div
                            class="mb-[6px] text-[12px] font-[500] leading-[16.8px] text-black"
                        >
                            {{ $t("vue.dmarc_record_ri_label") }}
                        </div>
                        <input
                            v-model="ui.ri"
                            class="dmarc-control"
                            type="number"
                            min="0"
                            step="1"
                        />
                        <div class="dmarc-hint">
                            {{ $t("vue.dmarc_record_ri_hint") }}
                        </div>
                    </div>
                </div>

                <div
                    class="grid grid-cols-[repeat(3,minmax(180px,1fr))] gap-[15px] max-[860px]:grid-cols-1"
                >
                    <div class="flex flex-col">
                        <div
                            class="mb-[6px] text-[12px] font-[500] leading-[16.8px] text-black"
                        >
                            {{ $t("vue.dmarc_record_adkim_label") }}
                        </div>
                        <select v-model="ui.adkimMode" class="dmarc-control">
                            <option value="">
                                {{ $t("vue.dmarc_record_option_not_set") }}
                            </option>
                            <option value="r">
                                {{ $t("vue.dmarc_record_option_relaxed") }}
                            </option>
                            <option value="s">
                                {{ $t("vue.dmarc_record_option_strict") }}
                            </option>
                        </select>
                        <div class="dmarc-hint">
                            {{ $t("vue.dmarc_record_alignment_hint") }}
                        </div>
                    </div>

                    <div class="flex flex-col">
                        <div
                            class="mb-[6px] text-[12px] font-[500] leading-[16.8px] text-black"
                        >
                            {{ $t("vue.dmarc_record_aspf_label") }}
                        </div>
                        <select v-model="ui.aspfMode" class="dmarc-control">
                            <option value="">
                                {{ $t("vue.dmarc_record_option_not_set") }}
                            </option>
                            <option value="r">
                                {{ $t("vue.dmarc_record_option_relaxed") }}
                            </option>
                            <option value="s">
                                {{ $t("vue.dmarc_record_option_strict") }}
                            </option>
                        </select>
                        <div class="dmarc-hint">
                            {{ $t("vue.dmarc_record_alignment_hint") }}
                        </div>
                    </div>

                    <div class="flex flex-col">
                        <div
                            class="mb-[6px] text-[12px] font-[500] leading-[16.8px] text-black"
                        >
                            {{ $t("vue.dmarc_record_fo_label") }}
                        </div>
                        <select v-model="ui.fo" class="dmarc-control">
                            <option value="">
                                {{ $t("vue.dmarc_record_option_not_set") }}
                            </option>
                            <option value="0">
                                {{ $t("vue.dmarc_record_option_fo_all") }}
                            </option>
                            <option value="1">
                                {{ $t("vue.dmarc_record_option_fo_any") }}
                            </option>
                            <option value="d">
                                {{ $t("vue.dmarc_record_option_fo_dkim") }}
                            </option>
                            <option value="s">
                                {{ $t("vue.dmarc_record_option_fo_spf") }}
                            </option>
                            <option
                                v-for="opt in foComboOptions"
                                :key="opt"
                                :value="opt"
                            >
                                {{ opt }}
                            </option>
                        </select>
                        <div class="dmarc-hint">
                            {{ $t("vue.dmarc_record_fo_hint") }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Step 2 - Replace the entire record with the new value -->
        <div>
            <div class="mb-[14px] flex items-start gap-3">
                <span class="step-badge">2</span>
                <div>
                    <div
                        class="text-[12px] font-semibold leading-[140%] text-gray-800"
                    >
                        {{ $t("vue.step_replace_record_title") }}
                    </div>
                    <p class="text-[10.5px] leading-[142%] text-gray-500">
                        {{ $t("vue.step_replace_record_desc") }}
                    </p>
                </div>
            </div>

            <div
                class="rounded-[8px] border border-[#E5E7EB] bg-[#F4F5F7] p-[21px]"
            >
                <div
                    class="grid w-full grid-cols-2 gap-[24px] max-[860px]:grid-cols-1"
                >
                    <div>
                        <div
                            class="text-[10.5px] font-[500] leading-[14.91px] text-[#9FA6B2]"
                        >
                            {{ $t("vue.record_type") }}
                        </div>
                        <div
                            class="mt-[6px] text-[12.75px] leading-[14.91px] text-[#161E2E]"
                        >
                            TXT
                        </div>
                    </div>
                    <div>
                        <div
                            class="text-[10.5px] font-[500] leading-[14.91px] text-[#9FA6B2]"
                        >
                            {{ $t("vue.host_name") }}
                        </div>
                        <div
                            class="mt-[6px] text-[12.75px] leading-[14.91px] text-[#161E2E]"
                        >
                            {{ hostName }}
                        </div>
                    </div>
                </div>

                <div class="mt-[24px] w-full">
                    <div
                        class="text-[10.5px] font-[500] leading-[14.91px] text-[#9FA6B2]"
                    >
                        {{ $t("vue.value") }}
                    </div>
                    <div
                        class="relative mt-[6px] rounded-[12.75px] border border-[#DDD] p-4"
                    >
                        <textarea
                            v-model="record"
                            class="w-full resize-y border-0 bg-transparent p-0 pr-[24px] text-[12.75px] leading-[16px] text-[#161E2E] outline-none"
                            rows="5"
                            spellcheck="false"
                            :aria-label="$t('vue.dmarc_record_aria_value')"
                            @blur="normalizeRecord"
                        />
                        <button
                            type="button"
                            class="absolute right-[12px] top-[12px] grid h-[14px] w-[14px] cursor-pointer place-items-center border-0 bg-transparent p-0"
                            :aria-label="$t('vue.dmarc_record_aria_copy')"
                            @click="copy"
                        >
                            <Copy class="h-[14px] w-[14px] text-[#161E2E]" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from "vue";
import { Copy } from "lucide-vue-next";
import { create } from "@/composables/useNotifications";
import { useTranslations } from "@/composables/useTranslations";
import {
    buildDmarc,
    parseDmarc,
    foComboOptions,
    type DmarcExtra,
    type DmarcKnown,
} from "@/composables/useDmarcRecord";

const props = defineProps({
    hostName: { type: String, default: "_dmarc" },
    modelValue: { type: String, default: "" },
});

const emit = defineEmits<{ "update:modelValue": [value: string] }>();
const { t } = useTranslations();

const ui = reactive({
    p: "none",
    pct: "100",
    ri: "86400",
    adkimMode: "",
    aspfMode: "",
    fo: "",
    rua: "",
    ruf: "",
    sp: "",
    np: "",
    rf: "",
    psd: "",
    t: "",
});

const extras = ref<DmarcExtra[]>([]);
const record = ref("");

const toKnownFromUi = (): DmarcKnown => ({
    p: ui.p,
    pct: ui.pct,
    ri: ui.ri,
    adkim: ui.adkimMode,
    aspf: ui.aspfMode,
    fo: ui.fo,
    rua: ui.rua,
    ruf: ui.ruf,
    sp: ui.sp,
    np: ui.np,
    rf: ui.rf,
    psd: ui.psd,
    t: ui.t,
});

const applyKnownToUi = (known: DmarcKnown): void => {
    ui.p = known.p;
    ui.pct = known.pct;
    ui.ri = known.ri;
    ui.adkimMode = known.adkim;
    ui.aspfMode = known.aspf;
    ui.fo = known.fo;
    ui.rua = known.rua;
    ui.ruf = known.ruf;
    ui.sp = known.sp;
    ui.np = known.np;
    ui.rf = known.rf;
    ui.psd = known.psd;
    ui.t = known.t;
};

let syncing = false;

watch(
    () => props.modelValue,
    (v) => {
        if (syncing) return;
        syncing = true;
        record.value = v?.trim()
            ? v
            : buildDmarc({ known: toKnownFromUi(), extras: extras.value });
        const parsed = parseDmarc(record.value);
        if (parsed.isDmarc) {
            applyKnownToUi(parsed.known);
            extras.value = parsed.extras;
        }
        syncing = false;
    },
    { immediate: true },
);

watch(
    () => ({ ...ui }),
    () => {
        if (syncing) return;
        syncing = true;
        record.value = buildDmarc({
            known: toKnownFromUi(),
            extras: extras.value,
        });
        emit("update:modelValue", record.value);
        syncing = false;
    },
    { deep: true, flush: "sync" },
);

watch(
    record,
    (v) => {
        if (syncing) return;
        const parsed = parseDmarc(v);
        if (!parsed.isDmarc) {
            emit("update:modelValue", v);
            return;
        }
        syncing = true;
        applyKnownToUi(parsed.known);
        extras.value = parsed.extras;
        emit("update:modelValue", v);
        syncing = false;
    },
    { flush: "sync" },
);

const normalizeRecord = (): void => {
    const parsed = parseDmarc(record.value);
    if (!parsed.isDmarc) return;
    syncing = true;
    applyKnownToUi(parsed.known);
    extras.value = parsed.extras;
    record.value = buildDmarc({ known: toKnownFromUi(), extras: extras.value });
    emit("update:modelValue", record.value);
    syncing = false;
};

const copy = async (): Promise<void> => {
    try {
        await navigator.clipboard.writeText(record.value);
    } catch {
        const ta = document.createElement("textarea");
        ta.value = record.value;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
    }
    create({ title: t("vue.dmarc_record_toast_copied"), type: "success" });
};
</script>

<style scoped>
.step-badge {
    display: inline-flex;
    height: 20px;
    width: 20px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    background-color: #088bea;
    font-size: 10px;
    font-weight: 600;
    color: #fff;
}

.dmarc-control {
    height: 36px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background-color: #fff;
    padding: 8px 10px;
    font-size: 12px;
    line-height: 16.8px;
    color: #161e2e;
    outline: none;
}

.dmarc-hint {
    margin-top: 6px;
    font-size: 10px;
    line-height: 14.2px;
    color: #9fa6b2;
}
</style>
